// app/providers.tsx
"use client";

import { usePathname } from "next/navigation";
import posthog, { type PostHogConfig } from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { subscribeToAnalyticsIdentity } from "@/lib/analytics/identity";
import { getEnvironment } from "@/lib/environment";
import { isPostHogEnabled } from "@/lib/posthog/is-posthog-enabled";
import { shouldSendPostHogEvents } from "@/lib/posthog/should-send-posthog-events";

// Local reads flags and sends nothing. `before_send` returning null drops every event the SDK
// would post, pageviews and `$identify` included. Replay and surveys are their own traffic.
const FLAGS_ONLY_OPTIONS = {
  before_send: () => null,
  disable_session_recording: true,
  disable_surveys: true,
} satisfies Partial<PostHogConfig>;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [isPostHogReady, setIsPostHogReady] = useState(false);

  useEffect(() => {
    // Without a key PostHog never initializes. Local initializes for flags only.
    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!apiKey || !isPostHogEnabled()) return;

    posthog.init(apiKey, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: false,
      // Autocapture records the text of whatever the user clicked ($el_text). On a chart that is
      // the patient's name, a diagnosis chip, a medication row, a lab value. Product events are
      // declared deliberately in lib/analytics/events.ts, so nothing here depends on it.
      autocapture: false,
      // Same reasoning as sentry.client.config.ts's replay `maskAllText`: an EHR screen is PHI.
      // posthog-js masks input values by default but NOT page text, and whether recording runs at
      // all is a PostHog project setting — so pin the masking in code rather than leave it to a
      // dashboard toggle that can be flipped without a review.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "*",
      },
      ...(shouldSendPostHogEvents() ? {} : FLAGS_ONLY_OPTIONS),
    });

    // Auto-attach environment to every event for sandbox vs production segmentation
    posthog.register({ environment: getEnvironment().name });

    // Identity is applied inside init, before `isPostHogReady` lets the first `$pageview` fire,
    // so every event of the session carries the role person properties and the practice group.
    // `identify` rather than `setPersonProperties`: under `person_profiles: "identified_only"` a
    // session whose PostHog cookie was cleared while the Medplum session survived is anonymous,
    // and person properties set on an anonymous session are dropped. `identify` repairs that and
    // is a no-op when the distinct id already matches.
    const unsubscribe = subscribeToAnalyticsIdentity((identity) => {
      posthog.identify(identity.distinctId, identity.personProperties);
      if (identity.group) {
        posthog.group(identity.group.type, identity.group.key, identity.group.properties);
      }
    });

    setIsPostHogReady(true);
    return unsubscribe;
  }, []);

  // Without PostHog, render the children alone
  if (!isPostHogEnabled()) {
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      {isPostHogReady ? <PostHogPageView /> : null}
      {children}
    </PHProvider>
  );
}

function PostHogPageView() {
  const pathname = usePathname();
  const posthog = usePostHog();

  // Track pageviews
  useEffect(() => {
    if (!shouldSendPostHogEvents()) return;

    if (pathname && posthog) {
      // Path only. Query strings on this app carry an invite `code` (a login secret plus a member
      // email) and a prewritten refill message that names a medication. An allowlist of safe params
      // would leak the next sensitive one by default, so send no query string at all. A param that
      // analytics genuinely needs goes here by name.
      posthog.capture("$pageview", { $current_url: window.origin + pathname });
    }
  }, [pathname, posthog]);

  return null;
}
